// ConsentGate — blocks all health-tracking UI until the signed-in user has
// granted Consumer Health Data consent. Checks the latest consent_events
// row for the current user; if not 'granted', shows a consent screen.
// On submit, inserts a new consent_events row (append-only audit log) and
// then renders children. Applies to existing users on their next visit.

import { useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CHD_CONSENT_TYPE, CHD_POLICY_VERSION } from "@/lib/consent";
import { DEV_AUTH_USER_ID } from "@/knowmeno/devAuthBypass";

type Status = "checking" | "needed" | "granted" | "error";

export function ConsentGate({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<Status>("checking");
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus("checking");
      if (userId === DEV_AUTH_USER_ID) {
        setStatus("granted");
        return;
      }
      const { data, error } = await supabase
        .from("consent_events")
        .select("action")
        .eq("user_id", userId)
        .eq("consent_type", CHD_CONSENT_TYPE)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error("[consent-gate] load failed", error);
        setStatus("error");
        return;
      }
      setStatus(data?.action === "granted" ? "granted" : "needed");
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function handleAgree() {
    setSubmitting(true);
    setErrMsg(null);
    const { error } = await supabase.from("consent_events").insert({
      user_id: userId,
      consent_type: CHD_CONSENT_TYPE,
      policy_version: CHD_POLICY_VERSION,
      action: "granted",
    });
    setSubmitting(false);
    if (error) {
      console.error("[consent-gate] insert failed", error);
      setErrMsg("We couldn't save your consent. Please try again.");
      return;
    }
    setStatus("granted");
  }

  if (status === "checking") {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6E4B71",
          fontFamily: "system-ui, sans-serif",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  if (status === "granted") return <>{children}</>;

  return (
    <div
      style={{
        maxWidth: 560,
        margin: "32px auto",
        background: "#FFFFFF",
        border: "1px solid #EADBE2",
        borderRadius: 16,
        padding: 24,
        fontFamily: "system-ui, sans-serif",
        color: "#3B1A3F",
        boxShadow: "0 4px 18px rgba(74, 31, 79, 0.06)",
      }}
    >
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 8px", color: "#4B1F4F" }}>
        Consent to track your health data
      </h1>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: "#5A3A5E", margin: "0 0 12px" }}>
        Before you start tracking, we need your explicit consent to collect and
        process your <strong>consumer health data</strong> — including symptoms,
        mood, cycle, HRT, lab results, and any wearable metrics you choose to
        connect — so we can show you trends and build a report you can share
        with your doctor.
      </p>
      <p style={{ fontSize: 13, lineHeight: 1.55, color: "#5A3A5E", margin: "0 0 12px" }}>
        We do not sell data that identifies you, and we do not use it to target ads to you. We may use de-identified, aggregated data — which cannot identify you — to run, improve, and grow KnowMeno; our Consumer Health Data Privacy Policy explains this. You can withdraw your permission and ask us to delete your data at any time.
      </p>
      <p style={{ fontSize: 13, lineHeight: 1.55, color: "#5A3A5E", margin: "0 0 20px" }}>
        Review our{" "}
        <a href="/consumer-health-privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#8A2A6B", textDecoration: "underline" }}>
          Consumer Health Data Privacy Policy
        </a>{" "}
        and{" "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#8A2A6B", textDecoration: "underline" }}>
          Privacy Policy
        </a>{" "}
        for full details.
      </p>

      {errMsg && (
        <div
          style={{
            background: "#FFE4EA",
            color: "#8A1F1F",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {errMsg}
        </div>
      )}

      {status === "error" && !errMsg && (
        <div
          style={{
            background: "#FFE4EA",
            color: "#8A1F1F",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          We couldn't check your consent status. You can still agree below to continue.
        </div>
      )}

      <button
        onClick={handleAgree}
        disabled={submitting}
        style={{
          width: "100%",
          background: submitting ? "#B07AA1" : "#8A2A6B",
          color: "#FFFFFF",
          border: "none",
          borderRadius: 10,
          padding: "12px 16px",
          fontSize: 15,
          fontWeight: 600,
          cursor: submitting ? "default" : "pointer",
        }}
      >
        {submitting ? "Saving…" : "I agree — start tracking"}
      </button>

      <p style={{ fontSize: 11, color: "#8A6E8E", margin: "12px 0 0", textAlign: "center" }}>
        Policy version: {CHD_POLICY_VERSION}
      </p>
    </div>
  );
}
